/**
 * Shared chrome for standalone auth / form pages.
 *
 * AuthFormCard always renders a horizontally centered BrandLogo so new form
 * pages inherit the same mark without per-page positioning. AuthBrandHeader
 * is the same mark for pages that supply their own card (onboarding, verify).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BrandLogo, type BrandLogoSize } from '@/components/brand/BrandLogo';
import { t } from '@/i18n';

interface AuthBrandHeaderProps {
  size?: BrandLogoSize;
  className?: string;
}

export function AuthBrandHeader({
  size = 'lg',
  className,
}: AuthBrandHeaderProps): React.ReactElement {
  return (
    <div className={cn('mb-8 flex w-full justify-center', className)}>
      <Link to="/" className="inline-flex max-w-full" aria-label={t('landing.nav.home')}>
        <BrandLogo size={size} align="center" />
      </Link>
    </div>
  );
}

interface AuthPageLayoutProps {
  children: React.ReactNode;
  /** Wider column for multi-step forms (onboarding). */
  wide?: boolean;
  className?: string;
}

export function AuthPageLayout({
  children,
  wide = false,
  className,
}: AuthPageLayoutProps): React.ReactElement {
  return (
    <div
      className={cn(
        'flex min-h-screen w-full items-center justify-center bg-gray-50 px-4 py-8',
        'overflow-y-auto pb-cookie-banner',
        className,
      )}
      data-testid="auth-page-layout"
    >
      <div className={cn('w-full', wide ? 'max-w-4xl' : 'max-w-md')}>
        {children}
      </div>
    </div>
  );
}

interface AuthFormCardProps {
  children: React.ReactNode;
  className?: string;
}

/** White card with the centered product lockup already in place. */
export function AuthFormCard({
  children,
  className,
}: AuthFormCardProps): React.ReactElement {
  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200 bg-white p-8 sm:p-10',
        className,
      )}
    >
      <AuthBrandHeader />
      {children}
    </div>
  );
}

AuthBrandHeader.displayName = 'AuthBrandHeader';
AuthPageLayout.displayName = 'AuthPageLayout';
AuthFormCard.displayName = 'AuthFormCard';
